import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, useParams } from "react-router-dom";
import { AppInput } from "../../../components/AppInput";
import { GlobalContainer } from "../../../components/GlobalContainer";
import { FormButton } from "../../../components/login/button.component";
import { IInvitedUserInfoResponse } from "../../../_services/interface/user.interface";
import { getBasicUserInfo, postCompleteRegistration } from "../../../_services/user.service";
import { FieldErrorsImpl, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormError, SimpleFormError } from "../../../components/login/formError.component";
import { IErrorResponse } from "../../../_services/interface/IDefaults";

interface ICompleteRegistrationInputs {
  name: string;
  password: string;
  confirmPassword: string;
}

export const CompleteRegistration = () => {
  const YupCompleteRegistrationForm = Yup.object().shape({
    name: Yup.string().required("Informe seu nome"),
    password: Yup.string().required("Informe o Password"),
    confirmPassword: Yup.string().required("Confirme o password"),
  });

  //Generate o resolver
  const resolverForm = { resolver: yupResolver(YupCompleteRegistrationForm) };

  const navigation = useNavigate();
  const { hash } = useParams();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const [userEmail, setUserEmail] = useState<string>();
  const [clientName, setClientName] = useState<string>();
  const [isDataLoaded, setDataLoaded] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState<FieldErrorsImpl>();
  const [captchaChallenge, setCaptchaChallenge] = useState<boolean>(false);
  const [smplErrorMessage, setSmplErrorMessage] = useState<string>();
  /** GEN-3739: convite inválido já no carregamento — o formulário nem faz sentido, some do lugar. */
  const [inviteError, setInviteError] = useState<string>();
  const [allFieldsFilled, setFieldsState] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [passwordStrength, setPasswordStrength] = useState<string>("#e8e8e8");
  const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})");
  const mediumRegex = new RegExp(
    "^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})"
  );

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<ICompleteRegistrationInputs>(resolverForm);

  /**
   * GEN-3739: a chamada roda em `useEffect`, uma vez por token.
   *
   * Ela era invocada no CORPO do componente, então disparava a cada render — e cada resposta
   * chama `setUserEmail`/`setClientName`, que re-renderizam e disparam de novo. Nos logs de PRD do
   * dia do incidente são ~25 requisições a `/users/invite-data/<token>` em poucos segundos para o
   * mesmo convidado, na tela em que ele está tentando entrar.
   */
  useEffect(() => {
    const getBasicInformation = async () => {
      const { status, response } = await getBasicUserInfo(hash as string);
      if (status === 200) {
        setUserEmail((response as IInvitedUserInfoResponse).email);
        setClientName((response as IInvitedUserInfoResponse).client);
      } else if ((response as IErrorResponse)?.message === "INVITE_INVALID") {
        /*
          GEN-3739: este é o caminho DOMINANTE do link substituído — quem clica num convite antigo
          nem chega ao formulário, porque o GET já devolve `INVITE_INVALID`. Redirecionar para `/`
          em silêncio (comportamento anterior) devolve a pessoa ao login sem dizer o que houve, e
          ela recomeça o mesmo ciclo. A mesma orientação do submit vale aqui, e com mais razão.
        */
        setInviteError(
          "Este link de convite expirou ou foi substituído por um mais recente. Abra o e-mail mais novo que enviamos e use o link de lá."
        );
      } else {
        //If fails, the system will send this user back to the navigation
        navigation("/");
      }
    };

    getBasicInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  useEffect(() => {
    if (clientName && userEmail) {
      setDataLoaded(true);
    }
  }, [clientName, userEmail]);

  useEffect(() => {
    if (errors) {
      setErrorMessage(errors as any);
    }
  }, [errors]);

  useEffect(() => {
    watch((value) => {
      if (Object.values(value).filter((_x) => _x !== "").length === 3) {
        setFieldsState(true);
      } else {
        setFieldsState(false);
      }

      if (value.password) {
        if (strongRegex.test(value.password)) {
          setPasswordStrength("#0F9D58");
        } else if (mediumRegex.test(value.password)) {
          setPasswordStrength("#F4B400");
        } else {
          setPasswordStrength("#DB4437");
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    if (allFieldsFilled && captchaChallenge) {
      //All fields are filled and captcha challenge completed
      //so the system will reveal the Button
      setSubmitDisabled(false);
    } else {
      //If nothing meets the system will keep the button disabled
      setSubmitDisabled(true);
    }
  }, [allFieldsFilled, captchaChallenge]);

  function onChangeCaptcha(value: any) {
    if (value) {
      setCaptchaChallenge(true);
    } else {
      setCaptchaChallenge(false);
    }
  }

  const handleFormInfo = async (data: ICompleteRegistrationInputs) => {
    //Disable everything
    setSubmitDisabled(true);
    //Check if the password is the same
    if (data.password === data.confirmPassword) {
      //Complete the registration
      const { status, response } = await postCompleteRegistration(hash as string, data);

      if (status === 200) {
        //Registration was ok, so the system will send this user to the login page
        navigation("/");
      } else if (status === 400 && (response as IErrorResponse)?.message === "Password must be stronger") {
        setSmplErrorMessage(
          "A senha precisa ter pelo menos 8 caracteres dentre eles letras maísculas, minusculas, números e simbolos."
        );
      } else if ((response as IErrorResponse)?.message === "INVITE_INVALID") {
        /*
          GEN-3739: o convite vira inválido também quando um MAIS NOVO é emitido — pedir um reenvio
          queima o link anterior (`getLinkInviteUser` marca o antigo como usado). Quem estava com
          esta aba aberta e clicou em "Reenviar" na tela de login cai exatamente aqui, e o genérico
          "tente novamente mais tarde" abaixo manda a pessoa esperar em vez de abrir o e-mail certo.
        */
        setSmplErrorMessage(
          "Este link de convite expirou ou foi substituído por um mais recente. Abra o e-mail mais novo que enviamos e use o link de lá."
        );
      } else {
        //Something happened and failed the request
        setSmplErrorMessage("Falha ao realizar o registro. Tente novamente mais tarde");
      }
    } else {
      reset({ password: "", confirmPassword: "" });
      setSmplErrorMessage("As senhas precisam ser iguais para prosseguir com o cadastro");
    }
    setSubmitDisabled(false);
  };

  return (
    <GlobalContainer translate="no" display="flex">
      <Flex id="teste" position="absolute" left="0" top="0" right="0" bottom="0">
        <Flex
          w="50%"
          backgroundImage="../images/loginImage-cropped.png"
          backgroundSize="contain"
          backgroundRepeat="no-repeat"
          backgroundAttachment="fixed"
          backgroundPosition="-25px center"
          justifyContent="center"
          alignItems="center"
        >
          <Flex fontFamily="Comfortaa" fontWeight="800">
            <Text color="white" m="0" fontSize="96px">
              taxly
            </Text>
          </Flex>
        </Flex>
        <Flex w="50%" justifyContent="center" backgroundColor="white" alignItems="center">
          <Flex fontFamily="Poppins" fontWeight={500} display="flex" alignItems="center" mt="0px" w={396}>
            <Flex direction="column" w="100%">
              <Text fontSize="32px" textAlign="center">
                Complete o Cadastro
              </Text>
              {inviteError ? (
                // GEN-3739: convite inválido no carregamento — mostra o motivo e o caminho, em vez
                // do formulário morto (ou do redirect silencioso que existia antes).
                <Flex direction="column" alignItems="center" marginTop="16px" gap={4}>
                  <SimpleFormError msg={inviteError} />
                  <Link href="/" fontSize="12px" textDecoration="underline">
                    Voltar para o login
                  </Link>
                </Flex>
              ) : (
                <>
                  <Flex alignItems="center" fontSize="12px" marginTop="10px" justifyContent="center">
                    <Text>
                      Você está vendo esta tela porque recebeu um convite em seu email <strong>{userEmail} </strong>
                      da empresa <strong>{clientName}</strong>. Ao terminar o seu cadastro você será redirecionado para
                      a tela de login.
                    </Text>
                  </Flex>
                  <Box marginTop={18} opacity={isDataLoaded ? 1.0 : 0.1}>
                    <form>
                      <Flex direction="column" alignItems="stretch">
                        {errorMessage ? <FormError message={errorMessage} /> : null}
                        {smplErrorMessage ? <SimpleFormError msg={smplErrorMessage} /> : null}

                        <Flex direction="column" gap={2}>
                          <AppInput placeholder="Nome" type="text" {...register("name", { required: true })} />
                        </Flex>
                        <Flex flexDirection="row" gap={2} mt={2} justifyContent="space-between">
                          <AppInput placeholder="Senha" type="password" {...register("password", { required: true })} />
                          <AppInput
                            placeholder="Confirmação de Senha"
                            type="password"
                            {...register("confirmPassword", { required: true })}
                          />
                        </Flex>
                        <Flex flexDirection="column" gap={2} justifyContent="space-between" mt={2}>
                          <Flex borderRadius={5} bg={passwordStrength} h={2}></Flex>
                          <Text fontSize={11} opacity={0.5}>
                            A senha precisa ter pelo menos 8 caracteres dentre eles letras maísculas, minusculas,
                            números e simbolos.
                          </Text>
                        </Flex>
                        <Flex direction="row" justifyContent="center" mt="18" mb="18">
                          <ReCAPTCHA
                            ref={captchaRef}
                            onChange={onChangeCaptcha}
                            sitekey={process.env.REACT_APP_SITE_KEY || "6LelrG4iAAAAAGZUt_aPuZp1cInj7M1f_pGD98fM"}
                          />
                        </Flex>
                        <FormButton onClick={handleSubmit(handleFormInfo)} disabled={submitDisabled}>
                          Finalizar Cadastro
                        </FormButton>
                      </Flex>
                    </form>
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </GlobalContainer>
  );
};
